import React, { useState, useEffect } from 'react';
import { standarPelayananService } from '../../../services/standarPelayananService';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaSearch, FaMinus, FaFileAlt
} from 'react-icons/fa';

const ManajemenStandarPelayanan = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterKategori, setFilterKategori] = useState('');

    const [formData, setFormData] = useState({
        jenis: '',
        kategori: '',
        dasar_hukum: [''],
        persyaratan: [''],
        prosedur: [''],
        waktu: '',
        biaya: '',
        produk: '',
        jumlah_pelaksana: '',
        order: 0,
        status: 'publish',
    });

    const loadData = async () => {
        setLoading(true);
        const params = {};
        if (searchTerm) params.search = searchTerm;
        if (filterKategori) params.kategori = filterKategori;

        const result = await standarPelayananService.getStandarList(params);
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            jenis: '',
            kategori: '',
            dasar_hukum: [''],
            persyaratan: [''],
            prosedur: [''],
            waktu: '',
            biaya: '',
            produk: '',
            jumlah_pelaksana: '',
            order: dataList.length + 1,
            status: 'publish',
        });
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            jenis: item.jenis || '',
            kategori: item.kategori || '',
            dasar_hukum: item.dasar_hukum && item.dasar_hukum.length > 0 ? item.dasar_hukum : [''],
            persyaratan: item.persyaratan && item.persyaratan.length > 0 ? item.persyaratan : [''],
            prosedur: item.prosedur && item.prosedur.length > 0 ? item.prosedur : [''],
            waktu: item.waktu || '',
            biaya: item.biaya || '',
            produk: item.produk || '',
            jumlah_pelaksana: item.jumlah_pelaksana || '',
            order: item.order || 0,
            status: item.status || 'publish',
        });
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle array fields
    const handleArrayChange = (field, index, value) => {
        const newArray = [...formData[field]];
        newArray[index] = value;
        setFormData(prev => ({ ...prev, [field]: newArray }));
    };

    const addArrayItem = (field) => {
        setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
    };

    const removeArrayItem = (field, index) => {
        if (formData[field].length <= 1) return;
        const newArray = formData[field].filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, [field]: newArray }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clean empty arrays
        const submitData = {
            ...formData,
            dasar_hukum: formData.dasar_hukum.filter(i => i.trim() !== ''),
            persyaratan: formData.persyaratan.filter(i => i.trim() !== ''),
            prosedur: formData.prosedur.filter(i => i.trim() !== ''),
        };

        setSubmitting(true);
        let result;
        if (editing) {
            result = await standarPelayananService.updateStandar(editing.id, submitData);
        } else {
            result = await standarPelayananService.createStandar(submitData);
        }

        if (result.success) {
            showNotif(result.data?.message || 'Data berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await standarPelayananService.deleteStandar(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'publish' ? 'draft' : 'publish';
        const result = await standarPelayananService.updateStatus(item.id, newStatus);
        if (result.success) {
            showNotif('Status berhasil diubah');
            loadData();
        }
    };

    // Get unique kategori
    const kategoriList = [...new Set(dataList.map(item => item.kategori))].filter(Boolean);

    const filteredData = dataList.filter(item => {
        const matchSearch = item.jenis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.produk?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchKategori = !filterKategori || item.kategori === filterKategori;
        return matchSearch && matchKategori;
    });

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Standar Pelayanan</h2>
                    <p className="text-gray-600">Kelola data standar pelayanan</p>
                </div>
                <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                    <FaPlus className="mr-2" /> Tambah Standar Pelayanan
                </button>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Search & Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari jenis pelayanan atau produk..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
                <select
                    value={filterKategori}
                    onChange={(e) => setFilterKategori(e.target.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="">Semua Kategori</option>
                    {kategoriList.map(kat => (
                        <option key={kat} value={kat}>{kat}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">No</th>
                                <th className="p-4">Jenis Pelayanan</th>
                                <th className="p-4">Kategori</th>
                                <th className="p-4">Waktu</th>
                                <th className="p-4">Biaya</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-gray-500">
                                        Belum ada data standar pelayanan
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4 text-gray-500">{index + 1}</td>
                                        <td className="p-4">
                                            <p className="font-semibold text-[#0F4C3A] max-w-md">{item.jenis}</p>
                                            {item.produk && (
                                                <p className="text-xs text-gray-400">Produk: {item.produk}</p>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs">
                                                {item.kategori}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">{item.waktu}</td>
                                        <td className="p-4 text-sm text-gray-600">{item.biaya}</td>
                                        <td className="p-4">
                                            <button
                                                onClick={() => handleToggleStatus(item)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${item.status === 'publish'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-yellow-100 text-yellow-700'
                                                    }`}
                                            >
                                                {item.status === 'publish' ? 'Publish' : 'Draft'}
                                            </button>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(item)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b z-10">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    {editing ? 'Edit Standar Pelayanan' : 'Tambah Standar Pelayanan'}
                                </h3>
                                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Jenis Pelayanan <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="jenis"
                                        value={formData.jenis}
                                        onChange={handleInputChange}
                                        rows="2"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: Pendaftaran Wajib Pajak Daerah"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Kategori <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="kategori"
                                            value={formData.kategori}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: PBB, Pajak Online, dll"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Urutan
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                {/* Dasar Hukum */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">Dasar Hukum</label>
                                        <button
                                            type="button"
                                            onClick={() => addArrayItem('dasar_hukum')}
                                            className="text-sm text-[#2E7D32] hover:text-[#4CAF50] flex items-center gap-1"
                                        >
                                            <FaPlus size={12} /> Tambah
                                        </button>
                                    </div>
                                    {formData.dasar_hukum.map((item, index) => (
                                        <div key={index} className="flex gap-2 mb-2">
                                            <textarea
                                                value={item}
                                                onChange={(e) => handleArrayChange('dasar_hukum', index, e.target.value)}
                                                rows="2"
                                                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] text-sm"
                                                placeholder={`Dasar hukum ke-${index + 1}`}
                                            />
                                            {formData.dasar_hukum.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeArrayItem('dasar_hukum', index)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                >
                                                    <FaMinus />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Persyaratan */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">Persyaratan</label>
                                        <button
                                            type="button"
                                            onClick={() => addArrayItem('persyaratan')}
                                            className="text-sm text-[#2E7D32] hover:text-[#4CAF50] flex items-center gap-1"
                                        >
                                            <FaPlus size={12} /> Tambah
                                        </button>
                                    </div>
                                    {formData.persyaratan.map((item, index) => (
                                        <div key={index} className="flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={(e) => handleArrayChange('persyaratan', index, e.target.value)}
                                                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] text-sm"
                                                placeholder={`Persyaratan ke-${index + 1}`}
                                            />
                                            {formData.persyaratan.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeArrayItem('persyaratan', index)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                >
                                                    <FaMinus />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Prosedur */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Sistem, Mekanisme dan Prosedur
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => addArrayItem('prosedur')}
                                            className="text-sm text-[#2E7D32] hover:text-[#4CAF50] flex items-center gap-1"
                                        >
                                            <FaPlus size={12} /> Tambah
                                        </button>
                                    </div>
                                    {formData.prosedur.map((item, index) => (
                                        <div key={index} className="flex gap-2 mb-2">
                                            <span className="w-8 h-11 flex items-center justify-center bg-[#0F4C3A] text-white rounded-lg font-bold text-sm flex-shrink-0">
                                                {index + 1}
                                            </span>
                                            <textarea
                                                value={item}
                                                onChange={(e) => handleArrayChange('prosedur', index, e.target.value)}
                                                rows="2"
                                                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] text-sm"
                                                placeholder={`Prosedur ke-${index + 1}`}
                                            />
                                            {formData.prosedur.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeArrayItem('prosedur', index)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                >
                                                    <FaMinus />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Info lainnya */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Waktu</label>
                                        <input
                                            type="text"
                                            name="waktu"
                                            value={formData.waktu}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: 1 hari"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Biaya</label>
                                        <input
                                            type="text"
                                            name="biaya"
                                            value={formData.biaya}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Rp 0,00"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Produk</label>
                                        <input
                                            type="text"
                                            name="produk"
                                            value={formData.produk}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: NPWPD"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Pelaksana</label>
                                        <input
                                            type="text"
                                            name="jumlah_pelaksana"
                                            value={formData.jumlah_pelaksana}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: 3 orang"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    >
                                        <option value="publish">Publish</option>
                                        <option value="draft">Draft</option>
                                        <option value="arsip">Arsip</option>
                                    </select>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Data?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.jenis}</strong>.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenStandarPelayanan;